import Papa from "papaparse";
import { createContext, useCallback, useContext, useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { fetchCSV } from "../utils/dataCache";
import { loadSteamSnapshot } from "../services/steamData";

// Routes that actually use Steam metadata/CCU — every other dashboard has
// its own data source and should never pay for this download.
const STEAM_DATA_PATHS = new Set(["/steam", "/steam_debug", "/steam-mobile", "/followers", "/search"]);
const STEAM_CARD_PATHS = new Set(["/steam", "/steam_debug", "/steam-mobile"]);
const needsSteamData = (pathname) =>
  STEAM_DATA_PATHS.has(pathname) || pathname.startsWith("/game/");

const DataContext = createContext();

export const useGlobalData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useGlobalData must be used within DataProvider");
  }
  return context;
};

// --- Helpers for normalization & light fuzz ---
function normalize(str = "") {
  return String(str)
    .toLowerCase()
    .normalize("NFKD")                       // split accents
    .replace(/[\u0300-\u036f]/g, "")        // strip diacritics
    .replace(/[^a-z0-9\s]/g, " ")           // drop punctuation/trademarks/etc.
    .replace(/\s+/g, " ")                   // collapse spaces
    .trim();
}

// very small/fast edit distance (cap work to short strings)
function editDistance(a, b) {
  const n = Math.min(a.length, 32);
  const m = Math.min(b.length, 32);
  const A = a.slice(0, n);
  const B = b.slice(0, m);
  const dp = new Array(m + 1).fill(0);
  for (let j = 0; j <= m; j++) dp[j] = j;
  for (let i = 1; i <= n; i++) {
    let prev = i - 1;
    dp[0] = i;
    for (let j = 1; j <= m; j++) {
      const tmp = dp[j];
      const cost = A[i - 1] === B[j - 1] ? 0 : 1;
      dp[j] = Math.min(
        dp[j] + 1,           // deletion
        dp[j - 1] + 1,       // insertion
        prev + cost          // substitution
      );
      prev = tmp;
    }
  }
  return dp[m];
}

const enrichSteamMetadata = g => {
  const box_art = g.ImageURL || g.image_url || g.box_art || g.imageUrl || "";
  const normName = normalize(g.name);
  return { ...g, box_art, _hasImage: Boolean(box_art), _normName: normName,
    _tokens: normName.split(" ").filter(Boolean) };
};

export const DataProvider = ({ children }) => {
  const location = useLocation();
  const loadedRef = useRef(false);
  const [metadata, setMetadata] = useState([]);
  const [latestCCU, setLatestCCU] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [latestCCUMap, setLatestCCUMap] = useState();
  const snapshotRef = useRef(null);
  const catalogRequestRef = useRef(null);
  const catalogReadyRef = useRef(false);

  const getSnapshot = useCallback(() => {
    if (!snapshotRef.current) {
      snapshotRef.current = loadSteamSnapshot().catch(error => {
        snapshotRef.current = null;
        throw error;
      });
    }
    return snapshotRef.current;
  }, []);

  const ensureSteamCatalog = useCallback(() => {
    if (catalogReadyRef.current) return Promise.resolve(true);
    if (catalogRequestRef.current) return catalogRequestRef.current;
    setCatalogLoading(true);
    setError(null);
    catalogRequestRef.current = getSnapshot()
      .then(snapshot => snapshot.loadCatalog())
      .then(rows => {
        setMetadata(rows.map(enrichSteamMetadata));
        catalogReadyRef.current = true;
        return true;
      })
      .catch(error => {
        console.error("Failed to load the full Steam catalog:", error);
        setError(error.message);
        return false;
      })
      .finally(() => {
        catalogRequestRef.current = null;
        setCatalogLoading(false);
      });
    return catalogRequestRef.current;
  }, [getSnapshot]);

  useEffect(() => {
    if (needsSteamData(location.pathname) && !STEAM_CARD_PATHS.has(location.pathname)) {
      ensureSteamCatalog();
    }
  }, [location.pathname, ensureSteamCatalog]);

  const excludedTypes = new Set([
    "episode",
    "hardware",
    "music",
    "series",
    "video",
    "unlisted",
    "not found",
  ]);

  useEffect(() => {
    if (!needsSteamData(location.pathname) || loadedRef.current) return;
    loadedRef.current = true;

    const loadCommonData = async () => {
      try {
        setLoading(true);

        const quickCards = STEAM_CARD_PATHS.has(location.pathname);
        const snapshotPromise = getSnapshot();
        const [initialMetadata, ccuText] = await Promise.all([
          quickCards ? snapshotPromise.then(snapshot => snapshot.loadCards()) : ensureSteamCatalog().then(ok => {
            if (!ok) throw new Error("The full Steam catalog could not be loaded.");
            return [];
          }),
          fetchCSV(
            "https://steam-scrape-production.s3.us-east-1.amazonaws.com/latest_ccu.csv.gz",
            { ttl: 2 * 60 * 1000 }
          ),
        ]);
        const cleanMetadata = initialMetadata.filter(
          (item) =>
            item.app_id &&
            item.name &&
            !excludedTypes.has(item.type?.toLowerCase())
        );

        if (!catalogReadyRef.current) setMetadata(cleanMetadata.map(enrichSteamMetadata));

        // ---- LATEST CCU ----
        const parsedCCU = Papa.parse(ccuText.trim(), {
          header: true,
          skipEmptyLines: true,
        }).data;

        const ccuMap = new Map(
          parsedCCU
            .filter((row) => row.app_id && !isNaN(parseInt(row.ccu)))
            .map((row) => [String(row.app_id), parseInt(row.ccu, 10)])
        );
        setLatestCCU(parsedCCU);
        setLatestCCUMap(ccuMap);
        setLoading(false);
      } catch (err) {
        console.error("❌ Failed to load global data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadCommonData();
  }, [location.pathname, getSnapshot, ensureSteamCatalog]);

  // --- Public helpers ---
  const getGameMetadata = (appId) =>
    metadata.find((game) => game.app_id === String(appId));

  const getGameCCU = (appId) => {
    const ccuData = latestCCU.find((game) => game.app_id === String(appId));
    return ccuData ? parseInt(ccuData.ccu || 0, 10) : 0;
  };

  // NEW fuzzy search
  const searchGames = (searchTerm, softCap = 500) => {
    if (!searchTerm) return [];
    const q = normalize(searchTerm);
    if (!q) return [];

    const qTokens = q.split(" ").filter(Boolean);

    const scored = [];
    for (const g of metadata) {
      const nameN = g._normName;
      let score = 0;

      // quick reject if none of the tokens appear anywhere
      const tokensHit = qTokens.every((qt) => nameN.includes(qt));
      if (!tokensHit) {
        // try a soft fuzzy gate: allow if a short edit distance to whole name
        const ed = editDistance(q, nameN);
        if (ed > Math.max(2, Math.ceil(q.length * 0.25))) continue;
        score += 10; // mild fuzzy pass
      }

      // strong signals
      if (nameN === q) score += 120;
      if (nameN.startsWith(q)) score += 100;
      if (nameN.includes(q)) score += 60;

      // token prefix matches
      let tokenMatches = 0;
      for (const qt of qTokens) {
        for (const nt of g._tokens) {
          if (nt.startsWith(qt)) {
            tokenMatches++;
            break;
          }
        }
      }
      score += tokenMatches * 15;

      // exact token presence
      let exactTokenHits = 0;
      for (const qt of qTokens) {
        if (g._tokens.includes(qt)) exactTokenHits++;
      }
      score += exactTokenHits * 8;

      // light edit distance boost on first ~32 chars
      const ed = editDistance(q, nameN);
      if (ed <= 1) score += 25;
      else if (ed === 2) score += 15;

      // prefer entries that have images (as requested)
      if (g._hasImage) score += 12;

      // small popularity bias if we have CCU
      const ccu = latestCCUMap?.get(String(g.app_id)) || 0;
      if (ccu > 0) score += Math.min(10, Math.floor(Math.log10(ccu + 1)));

      if (score > 0) scored.push([score, g]);
    }

    // Sort by score desc, then hasImage desc, then name asc for stability
    scored.sort((a, b) => {
      if (b[0] !== a[0]) return b[0] - a[0];
      if (b[1]._hasImage !== a[1]._hasImage) return b[1]._hasImage ? 1 : -1;
      return a[1].name.localeCompare(b[1].name);
    });

    // Return a soft-capped list to avoid giant arrays; UI can paginate
    return scored.slice(0, softCap).map((s) => s[1]);
  };

  const getAllTags = () => {
    const tagSet = new Set();
    metadata.forEach((game) => {
      if (game.tags) {
        game.tags.split(",").forEach((tag) => {
          const cleanTag = tag.trim();
          if (cleanTag) tagSet.add(cleanTag);
        });
      }
    });
    return [...tagSet].sort();
  };

  const getAllPublishers = () => {
    const publisherSet = new Set();
    metadata.forEach((game) => {
      if (game.publishers) {
        game.publishers.split(/[,;]/).forEach((publisher) => {
          const cleanPublisher = publisher.trim();
          if (cleanPublisher) publisherSet.add(cleanPublisher);
        });
      }
    });
    return [...publisherSet].sort();
  };

  const getAllDevelopers = () => {
    const developerSet = new Set();
    metadata.forEach((game) => {
      if (game.developers) {
        game.developers.split(/[,;]/).forEach((developer) => {
          const cleanDeveloper = developer.trim();
          if (cleanDeveloper) developerSet.add(cleanDeveloper);
        });
      }
    });
    return [...developerSet].sort();
  };

  const getAllReleaseDates = () => {
    const dates = metadata
      .filter((game) => {
        if (!game) return false;
        const type = (game.type || "").toLowerCase();
        if (excludedTypes.has(type)) return false;
        const appId = String(game.app_id);
        if (!appId || appId === "0") return false;
        const ccu = latestCCUMap?.get(appId) || 0;
        if (ccu <= 0) return false;
        return true;
      })
      .map((game) => {
        const dateStr = game.release_date?.trim();
        if (!dateStr) return null;
        const date = new Date(dateStr);
        return isNaN(date.getTime()) ? null : date;
      })
      .filter(Boolean)
      .sort((a, b) => a - b);

    if (dates.length === 0) return;
    return [dates[0], dates[dates.length - 1]];
  };

  const getAllPrices = () => {
    const prices = metadata
      .filter((game) => {
        if (!game) return false;
        const type = (game.type || "").toLowerCase();
        if (excludedTypes.has(type)) return false;
        const appId = String(game.app_id);
        if (!appId || appId === "0") return false;
        const ccu = latestCCUMap?.get(appId) || 0;
        if (ccu <= 0) return false;
        return true;
      })
      .map((game) => parseFloat(game.full_price || 0))
      .filter((p) => !isNaN(p) && p >= 0);

    if (prices.length === 0) return [0, 0];
    return [Math.min(...prices), Math.max(...prices)];
  };

  const value = {
    metadata,
    latestCCU,
    loading,
    catalogLoading,
    catalogReady: catalogReadyRef.current,
    ensureSteamCatalog,
    error,
    getGameMetadata,
    getGameCCU,
    searchGames,          // ← new fuzzy version
    getAllTags,
    getAllPublishers,
    getAllDevelopers,
    getAllReleaseDates,
    getAllPrices,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
